import * as React from 'react';
import React__default, { memo, useMemo, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { Heading, Text } from '@grandvisionhq/elements';
import { Icon } from '@grandvisionhq/icons';
import { useIntl } from '@grandvisionhq/state';
import { IconLink, Util, Loader, Tabs, Tab, MediaQuery, Button, GoogleMaps, toast } from '@grandvisionhq/common';
import { DateUtils } from '@grandvisionhq/utils';
import { gql, useLazyQuery } from '@grandvisionhq/graphql';

var defaultValue = {
    MINIMAL: [],
    SMALL: [],
    MEDIUM: [],
    LARGE: [],
    COMPLETE: [],
};
var AddressConfigurationContext = React__default.createContext(defaultValue);
var AddressConfigurationProvider = function (_a) {
    var children = _a.children, _b = _a.settings, settings = _b === void 0 ? defaultValue : _b;
    return (React__default.createElement(AddressConfigurationContext.Provider, { value: settings }, children));
};
var useAddressConfiguration = function () { return React__default.useContext(AddressConfigurationContext); };

var LocationVariant;
(function (LocationVariant) {
    LocationVariant["ListItem"] = "ListItem";
    LocationVariant["StandAlone"] = "StandAlone";
    LocationVariant["MapItem"] = "MapItem";
})(LocationVariant || (LocationVariant = {}));

var ADDRESS_CONFIGURATION_SIZES = [
    'MINIMAL',
    'SMALL',
    'MEDIUM',
    'LARGE',
    'COMPLETE',
];
var OPENING_HOURS_DAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];
var OpenClosedStatus;
(function (OpenClosedStatus) {
    OpenClosedStatus["Open"] = "OPEN";
    OpenClosedStatus["Closing"] = "CLOSING";
    OpenClosedStatus["Closed"] = "CLOSED";
})(OpenClosedStatus || (OpenClosedStatus = {}));

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ADDRESS_CONFIGURATION_SIZES: ADDRESS_CONFIGURATION_SIZES,
  OPENING_HOURS_DAYS: OPENING_HOURS_DAYS,
  get OpenClosedStatus () { return OpenClosedStatus; },
  get LocationVariant () { return LocationVariant; }
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.
Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.
THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}
function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || from);
}
function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var Address = function (_a) {
    var className = _a.className, rows = _a.rows, children = _a.children, rest = __rest(_a, ["className", "rows", "children"]);
    return rows.length || Boolean(children) || (Array.isArray(children) && children.length > 0) ? (React__default.createElement("address", __assign({}, rest, { className: classnames('address-stores-v2', className) }),
        rows.map(function (row, index) { return (React__default.createElement("span", { key: row + "-" + index, className: "address-stores-v2__row" }, row)); }),
        children)) : null;
};

var getAddressRows = function (size, config) {
    return config[size];
};
var parseAddress = function (addressRows, addressData) {
    var replacements = Object.entries(addressData).map(function (_a) {
        var _b = __read(_a, 2), key = _b[0], value = _b[1];
        return ({
            regExp: new RegExp("{" + key + "}", 'g'),
            value: value,
        });
    });
    var inject = function (row, replacements) {
        return replacements.reduce(function (acc, _a) {
            var regExp = _a.regExp, value = _a.value;
            return (value === undefined ? acc : acc.replace(regExp, String(value)));
        }, row);
    };
    var clean = function (row) {
        var unusedTemplateRegExp = new RegExp(/{([^}]+)}/, 'g');
        return row
            .trim()
            .replace(unusedTemplateRegExp, '')
            .split(',')
            .map(function (el) { return el.trim(); })
            .filter(function (el) { return !!el; })
            .join(', ');
    };
    return addressRows.map(function (row) { return inject(row, replacements); }).map(clean);
};

var AddressContainer = function (_a) {
    var _b = _a.size, size = _b === void 0 ? 'COMPLETE' : _b, _c = _a.data, data = _c === void 0 ? {} : _c, rest = __rest(_a, ["size", "data"]);
    var config = useAddressConfiguration();
    var rows = React__default.useMemo(function () {
        var templateRows = getAddressRows(size, config);
        if (templateRows.length) {
            return parseAddress(templateRows, data);
        }
        var feedback = "No configuration for address size " + size;
        console.error(feedback);
        return ["[" + feedback + "]"];
    }, [size, config, data]);
    return React__default.createElement(Address, __assign({}, rest, { rows: rows }));
};

var DefaultLocationVariant = LocationVariant.ListItem;
var Location = function (_a) {
    var className = _a.className, id = _a.id, _b = _a.variant, variant = _b === void 0 ? DefaultLocationVariant : _b, name = _a.name, addressSize = _a.addressSize, addressData = _a.addressData, emailAddress = _a.emailAddress, phone = _a.phone, phoneHidden = _a.phoneHidden, emailHidden = _a.emailHidden, _c = _a.highlight, highlight = _c === void 0 ? false : _c, _d = _a.distanceDetails, distanceDetails = _d === void 0 ? null : _d, formattedDistance = _a.formattedDistance, _e = _a.note, note = _e === void 0 ? null : _e, _f = _a.buttons, buttons = _f === void 0 ? null : _f, openingHours = _a.openingHours, locationDetailsUrl = _a.locationDetailsUrl, wheelchairAccessible = _a.wheelchairAccessible, onCloseClick = _a.onCloseClick, onNameClick = _a.onNameClick, // @deprecated
    formattedAddress = _a.formattedAddress, // @deprecated
    primaryButton = _a.primaryButton, // @deprecated
    secondaryButton = _a.secondaryButton, // @deprecated
    rest = __rest(_a, ["className", "id", "variant", "name", "addressSize", "addressData", "emailAddress", "phone", "phoneHidden", "emailHidden", "highlight", "distanceDetails", "formattedDistance", "note", "buttons", "openingHours", "locationDetailsUrl", "wheelchairAccessible", "onCloseClick", "onNameClick", "formattedAddress", "primaryButton", "secondaryButton"]);
    var getLabel = useIntl().getLabel;
    var showFormattedAddress = formattedAddress && (!addressSize || !addressData);
    return (React__default.createElement("article", __assign({}, rest, { id: id !== undefined ? "" + id : id, className: classnames('location-item', {
            'location-item--highlight': highlight,
            'location-item--list-item': variant === LocationVariant.ListItem,
            'location-item--stand-alone': variant === LocationVariant.StandAlone,
            'location-item--map-item': variant === LocationVariant.MapItem,
        }, className) }),
        React__default.createElement("div", { className: "location-item__part" },
            React__default.createElement("div", { className: "location-item__content" },
                React__default.createElement(Heading, { className: "location-item__heading", component: variant === LocationVariant.ListItem || variant === LocationVariant.MapItem
                        ? 'h2'
                        : 'h1', variant: variant === LocationVariant.ListItem ? 'smallest' : 'small' }, renderHeadingContent()),
                variant === LocationVariant.MapItem && onCloseClick && (React__default.createElement("div", { className: "location-item__close-button", "data-t": "close-button" },
                    React__default.createElement("button", { onClick: onCloseClick },
                        React__default.createElement(Icon, { name: "close" })))),
                React__default.createElement("div", { className: "location-item__address", "data-t": "location-item-address" }, showFormattedAddress ? (React__default.createElement("address", null, formattedAddress)) : (React__default.createElement(AddressContainer, { size: addressSize, data: addressData },
                    React__default.createElement("div", { className: "location-item__contact" },
                        !phoneHidden && phone && (React__default.createElement("div", { className: "location-item__contact-field" },
                            React__default.createElement("a", { href: "tel:" + phone, "data-t": "Phone" }, phone))),
                        !emailHidden && emailAddress && (React__default.createElement("div", { className: "location-item__contact-field" },
                            React__default.createElement("a", { href: "mailto:" + emailAddress, "data-t": "Email" }, emailAddress))))))),
                distanceDetails && (React__default.createElement("div", { className: "location-item__distance-details" }, distanceDetails)),
                wheelchairAccessible && (React__default.createElement("div", { className: "location-item__wheelchair-accessible" },
                    React__default.createElement(Icon, { name: "wheelchair", className: "location-item__wheelchair-accessible__icon" }),
                    getLabel('stores.page.details.wheelchairAccessible'))),
                formattedDistance && (React__default.createElement("span", { className: "location-item__distance", "data-t": "location-item-distance" }, formattedDistance))),
            note,
            variant === LocationVariant.StandAlone && (React__default.createElement("div", { className: "location-item__actions" },
                secondaryButton,
                primaryButton,
                buttons))),
        React__default.createElement("div", { className: "location-item__part" },
            React__default.createElement("div", { className: "location-item__opening-hours" }, openingHours),
            (variant === LocationVariant.ListItem || variant === LocationVariant.MapItem) && (React__default.createElement("div", { className: "location-item__actions" },
                secondaryButton,
                primaryButton,
                buttons)))));
    function renderHeadingContent() {
        if (locationDetailsUrl) {
            return (React__default.createElement("a", { "data-t": "location-link-" + id, className: "location-item__link", href: locationDetailsUrl }, name));
        }
        if (onNameClick) {
            return (React__default.createElement("button", { "data-t": "location-link-" + id, className: "location-item__link", onClick: function () { return onNameClick(id); } }, name));
        }
        return name;
    }
};

var scheme = {
    phone: 'tel',
    email: 'mailto',
};
var icon = {
    phone: 'phone',
    email: 'mail',
};
var LocationContactField = function (_a) {
    var _b = _a.className, className = _b === void 0 ? '' : _b, fieldType = _a.fieldType, value = _a.value;
    return (React.createElement(IconLink, { className: className, href: scheme[fieldType] + ":" + value, iconName: icon[fieldType] }, value));
};

function LocationDistance(_a) {
    var className = _a.className, distance = _a.distance, labels = _a.labels, timeByCar = _a.timeByCar, timeByFoot = _a.timeByFoot, rest = __rest(_a, ["className", "distance", "labels", "timeByCar", "timeByFoot"]);
    var isEmpty = !(distance || timeByCar || timeByFoot);
    if (isEmpty)
        return null;
    return (React__default.createElement("div", __assign({ className: classnames('location-distance', className) }, rest),
        React__default.createElement("div", { className: "location-distance__content" },
            distance && (React__default.createElement("div", { className: "location-distance__item" },
                React__default.createElement(Text, { className: "location-distance__text", component: "span", variant: "body-head" }, labels.distance))),
            timeByFoot && (React__default.createElement("div", { className: "location-distance__item" },
                React__default.createElement(Icon, { name: "walking" }),
                React__default.createElement("span", { className: "location-distance__visually-hidden" }, labels.walking),
                React__default.createElement(Text, { className: "location-distance__text", component: "span" }, timeByFoot))),
            timeByCar && (React__default.createElement("div", { className: "location-distance__item" },
                React__default.createElement(Icon, { name: "driving" }),
                React__default.createElement("span", { className: "location-distance__visually-hidden" }, labels.driving),
                React__default.createElement(Text, { className: "location-distance__text location-distance__text--space", component: "span" }, timeByCar))))));
}
var LocationDistance$1 = memo(LocationDistance);

function LocationDistanceContainer(_a) {
    var distance = _a.distance, _b = _a.labels, labels = _b === void 0 ? {} : _b, rest = __rest(_a, ["distance", "labels"]);
    var _c = useIntl(), getLabel = _c.getLabel, getFormattedLabel = _c.getFormattedLabel;
    var defaultLabels = useMemo(function () { return (__assign({ distance: getFormattedLabel('stores.location.distance', {
            labelData: { distance: distance || '' },
        }), driving: getLabel('stores.location.drivingTime'), walking: getLabel('stores.location.walkingTime') }, labels)); }, [distance, labels, getFormattedLabel, getLabel]);
    return React__default.createElement(LocationDistance$1, __assign({ distance: distance, labels: defaultLabels }, rest));
}
var LocationDistanceContainer$1 = memo(LocationDistanceContainer);

var SkipToMapLink = function (_a) {
    var onClick = _a.onClick, text = _a.text, href = _a.href, rest = __rest(_a, ["onClick", "text", "href"]);
    return (React.createElement("div", __assign({}, rest, { className: "locations-skip-to-map-link" }),
        React.createElement("a", { onClick: onClick, href: href, className: "locations-skip-to-map-link__link" }, text)));
};

var SkipToMapLinkContainer = function (_a) {
    var mapId = _a.mapId, rest = __rest(_a, ["mapId"]);
    var getLabel = useIntl().getLabel;
    // We cannot create a ref for the tabindex div that needs to be focused because it is generated
    // from the GoogleMaps library. Therefore, focus is managed here with vanilla js instead.
    var onClick = function (event) {
        var _a, _b;
        event.preventDefault();
        (_b = (_a = document.getElementById(mapId)) === null || _a === void 0 ? void 0 : _a.querySelector('[tabindex="0"]')) === null || _b === void 0 ? void 0 : _b.focus();
    };
    return (React.createElement(SkipToMapLink, __assign({}, rest, { href: "#" + mapId, onClick: onClick, text: getLabel('stores.page.search.skipToMapLink') })));
};

var LocationList = function (_a) {
    var children = _a.children, scrollToTargetId = _a.scrollToTargetId, scrollContainer = _a.scrollContainer;
    var locationRefs = useRef({});
    useEffect(function () {
        if (typeof scrollToTargetId === 'number') {
            var targetElem = locationRefs.current[scrollToTargetId];
            if (targetElem) {
                var offset = targetElem.offsetTop;
                // If we have a scrollContainer element provided, scroll inside that,
                // if not, we scroll the window to the target offset
                if (scrollContainer) {
                    Util.scrollToItem(scrollContainer, offset, 500);
                }
                else {
                    window.scrollTo(0, offset);
                }
            }
        }
    }, [scrollToTargetId, scrollContainer]);
    return (React__default.createElement("ul", { className: "location-list" }, React__default.Children.map(children, function (child) { return (React__default.createElement("li", { key: child.key, className: "location-list__item", ref: function (el) {
            var _a;
            locationRefs.current[(_a = child.key) !== null && _a !== void 0 ? _a : ''] = el;
        } }, child)); })));
};

var LocationSearchList = function (_a) {
    var children = _a.children, className = _a.className, loading = _a.loading, rest = __rest(_a, ["children", "className", "loading"]);
    return (React__default.createElement("div", __assign({}, rest, { className: classnames('location-search-list', className) }), loading ? (React__default.createElement("div", { className: "location-search-list__loader" },
        React__default.createElement(Loader, null))) : (React__default.createElement(LocationList, null, children))));
};

var LocationSearchListLayout = function (_a) {
    var className = _a.className, loadingListItems = _a.loadingListItems, listItems = _a.listItems, topContent = _a.topContent, searchForm = _a.searchForm, showTabs = _a.showTabs, infoContent = _a.infoContent, showLinkToMap = _a.showLinkToMap, googleMapsId = _a.googleMapsId, rest = __rest(_a, ["className", "loadingListItems", "listItems", "topContent", "searchForm", "showTabs", "infoContent", "showLinkToMap", "googleMapsId"]);
    return (React__default.createElement("div", __assign({}, rest, { className: classnames('location-search-list-layout', className) }),
        topContent,
        React__default.createElement("div", { className: classnames('location-search-list-layout__search-form', {
                'location-search-list-layout__search-form--hidden': showTabs,
            }) },
            searchForm,
            showLinkToMap && googleMapsId && React__default.createElement(SkipToMapLinkContainer, { mapId: googleMapsId })),
        React__default.createElement(LocationSearchList, { loading: loadingListItems }, listItems),
        infoContent));
};

var LocationSearchTabs = function (_a) {
    var className = _a.className, labels = _a.labels, tabIndex = _a.tabIndex, onChange = _a.onChange, listContent = _a.listContent, mapContent = _a.mapContent, _b = _a.showTabs, showTabs = _b === void 0 ? false : _b, tabsRef = _a.tabsRef, rest = __rest(_a, ["className", "labels", "tabIndex", "onChange", "listContent", "mapContent", "showTabs", "tabsRef"]);
    return (React__default.createElement("div", __assign({}, rest, { ref: tabsRef, className: classnames('location-search-tabs', className) }),
        React__default.createElement(Tabs, { activeTabIndex: tabIndex, onChange: onChange },
            React__default.createElement(Tab, { className: classnames('location-search-tabs__tab', {
                    'location-search-tabs__tab--hidden': !showTabs,
                }), label: React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Icon, { className: "location-search-tabs__tab-icon", name: "list" }),
                    React__default.createElement("span", { className: "location-search-tabs__tab-label" }, labels.listTab)) }),
            React__default.createElement(Tab, { onClick: function () { var _a; return (_a = tabsRef === null || tabsRef === void 0 ? void 0 : tabsRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView(); }, className: classnames('location-search-tabs__tab', {
                    'location-search-tabs__tab--hidden': !showTabs,
                }), label: React__default.createElement(React__default.Fragment, null,
                    React__default.createElement(Icon, { className: "location-search-tabs__tab-icon", name: "location" }),
                    React__default.createElement("span", { className: "location-search-tabs__tab-label" }, labels.mapTab)) })),
        React__default.createElement("div", { className: "location-search-tabs__content" },
            React__default.createElement("div", { className: classnames('location-search-tabs__content-item', 'location-search-tabs__content-item-list', {
                    'location-search-tabs__content-item--active': tabIndex === 0,
                }) }, listContent),
            React__default.createElement("div", { className: classnames('location-search-tabs__content-item', 'location-search-tabs__content-item-map', {
                    'location-search-tabs__content-item--active': tabIndex === 1,
                }) }, mapContent))));
};

var LocationSearchTabsContainer = function (props) {
    var _a;
    var getLabel = useIntl().getLabel;
    var _b = __read(React__default.useState(0), 2), tabIndex = _b[0], setTabIndex = _b[1];
    var ref = React__default.createRef();
    var labels = React__default.useMemo(function () { return ({
        listTab: getLabel('locations.search.tabs.list'),
        mapTab: getLabel('locations.search.tabs.map'),
    }); }, [getLabel]);
    return (React__default.createElement(LocationSearchTabs, __assign({}, props, { tabsRef: ref, labels: labels, showTabs: (_a = props.showTabs) !== null && _a !== void 0 ? _a : false, tabIndex: tabIndex, onChange: setTabIndex })));
};

var LocationSearch = function (_a) {
    var className = _a.className, searchForm = _a.searchForm, listContent = _a.listContent, listTopContent = _a.listTopContent, mapContent = _a.mapContent, _b = _a.showTabs, showTabs = _b === void 0 ? false : _b, scrollToHandlerId = _a.scrollToHandlerId, rest = __rest(_a, ["className", "searchForm", "listContent", "listTopContent", "mapContent", "showTabs", "scrollToHandlerId"]);
    return (React__default.createElement("article", __assign({}, rest, { className: classnames('location-search', className) }),
        listTopContent,
        showTabs && (React__default.createElement("div", { id: scrollToHandlerId, className: "location-search__search-form" }, searchForm)),
        React__default.createElement("div", { className: "location-search__wrapper" },
            React__default.createElement(LocationSearchTabsContainer, { showTabs: showTabs, listContent: listContent, mapContent: mapContent }))));
};

var LocationSearchContainer = function (_a) {
    var searchForm = _a.searchForm, loadingListItems = _a.loadingListItems, listItems = _a.listItems, listTopContent = _a.listTopContent, listInfoContent = _a.listInfoContent, mapContent = _a.mapContent, isPristineSearch = _a.isPristineSearch, googleMapsId = _a.googleMapsId, rest = __rest(_a, ["searchForm", "loadingListItems", "listItems", "listTopContent", "listInfoContent", "mapContent", "isPristineSearch", "googleMapsId"]);
    var isMobile = MediaQuery.useIsPhone();
    var showTabs = isMobile && !isPristineSearch;
    return (React__default.createElement(LocationSearch, __assign({}, rest, { showTabs: showTabs, searchForm: searchForm, listTopContent: isMobile && listTopContent, scrollToHandlerId: "location-search-form", listContent: React__default.createElement(LocationSearchListLayout, { googleMapsId: googleMapsId, showLinkToMap: !isMobile, topContent: !isMobile && listTopContent ? listTopContent : null, searchForm: searchForm, showTabs: showTabs, infoContent: listInfoContent !== null && listInfoContent !== void 0 ? listInfoContent : null, loadingListItems: loadingListItems, listItems: listItems }), mapContent: mapContent })));
};

var LocationNote = function (_a) {
    var className = _a.className, note = _a.note, rest = __rest(_a, ["className", "note"]);
    return note ? (React__default.createElement("small", __assign({}, rest, { className: classnames('location-note', className) }), note)) : null;
};

var getDayName$1 = function (index) { return OPENING_HOURS_DAYS[index]; };
var getHoursAndMinutes = function (time) {
    return time.split(time.indexOf('.') > -1 ? '.' : ':').map(function (t) { return parseInt(t); });
};
var getSpecialOpeningHoursForDate = function (specialOpeningHours, date) {
    return specialOpeningHours === null || specialOpeningHours === void 0 ? void 0 : specialOpeningHours.find(function (_a) {
        var dateOpenClosed = _a.dateOpenClosed;
        return new Date(dateOpenClosed).getDate() === date.getDate() &&
            new Date(dateOpenClosed).getMonth() === date.getMonth();
    });
};
var getRegularOpeningHoursForDate = function (openingHours, date) {
    return openingHours[getDayName$1(date.getDay())];
};
var getDateFromTime = function (time) {
    var _a = __read(getHoursAndMinutes(time), 2), hours = _a[0], minutes = _a[1];
    var date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    return date;
};
var isOpen = function (hours) {
    var now = new Date();
    return hours === null || hours === void 0 ? void 0 : hours.some(function (_a) {
        var open = _a.open, close = _a.close;
        var openDate = getDateFromTime(open);
        var closeDate = getDateFromTime(close);
        return now.getTime() >= openDate.getTime() && now.getTime() < closeDate.getTime();
    });
};
var isClosingSoon = function (hours) {
    var now = new Date();
    return hours === null || hours === void 0 ? void 0 : hours.some(function (_a) {
        var close = _a.close;
        var closeDate = getDateFromTime(close);
        var diff = Math.floor((closeDate.getTime() - now.getTime()) / 1000 / 60);
        return diff > 0 && diff < 60;
    });
};
var isClosed = function (hours) {
    var now = new Date();
    return hours === null || hours === void 0 ? void 0 : hours.every(function (_a) {
        var close = _a.close;
        var closeDate = getDateFromTime(close);
        return now.getTime() > closeDate.getTime();
    });
};
var getOpenCloseStatus = function (openingHours, specialOpeningHours) {
    var now = new Date();
    var openingHoursToday = getRegularOpeningHoursForDate(openingHours, now);
    var specialOpeningHoursToday = getSpecialOpeningHoursForDate(specialOpeningHours, now);
    var open, closingSoon, closed;
    if (specialOpeningHoursToday) {
        open =
            specialOpeningHoursToday.openClosed === 'OPEN' &&
                isOpen(specialOpeningHoursToday.openClosedTime);
        closingSoon = isClosingSoon(specialOpeningHoursToday.openClosedTime);
        closed = !open || isClosed(specialOpeningHoursToday.openClosedTime);
    }
    else {
        open = isOpen(openingHoursToday);
        closingSoon = isClosingSoon(openingHoursToday);
        closed = isClosed(openingHoursToday);
    }
    if (closed) {
        return OpenClosedStatus.Closed;
    }
    if (closingSoon) {
        return OpenClosedStatus.Closing;
    }
    return open ? OpenClosedStatus.Open : OpenClosedStatus.Closed;
};
var getOpenCloseIndication = function (openingHours, specialOpeningHours, timeFormatter) {
    var _a, _b, _c, _d;
    var today = new Date();
    var tomorrow = DateUtils.shiftDate(today, 'day', 1);
    var status = getOpenCloseStatus(openingHours, specialOpeningHours);
    var specialOpeningHoursToday = getSpecialOpeningHoursForDate(specialOpeningHours, today);
    var specialOpeningHoursTomorrow = getSpecialOpeningHoursForDate(specialOpeningHours, tomorrow);
    var regularOpeningHoursToday = getRegularOpeningHoursForDate(openingHours, today);
    var regularOpeningHoursTomorrow = getRegularOpeningHoursForDate(openingHours, tomorrow);
    if (status === OpenClosedStatus.Open || status === OpenClosedStatus.Closing) {
        return {
            hours: (_a = ((specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : specialOpeningHoursToday.openClosedTime) || regularOpeningHoursToday)) === null || _a === void 0 ? void 0 : _a.map(function (_a) {
                var open = _a.open, close = _a.close;
                return [
                    timeFormatter(getDateFromTime(open)),
                    timeFormatter(getDateFromTime(close)),
                ];
            }),
        };
    }
    if (status === OpenClosedStatus.Closed) {
        var nextHours = void 0;
        if ((specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : specialOpeningHoursToday.openClosed) === OpenClosedStatus.Open ||
            (regularOpeningHoursToday === null || regularOpeningHoursToday === void 0 ? void 0 : regularOpeningHoursToday.length)) {
            if ((_b = specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : specialOpeningHoursToday.openClosedTime) === null || _b === void 0 ? void 0 : _b.length) {
                nextHours = (_c = specialOpeningHoursToday === null || specialOpeningHoursToday === void 0 ? void 0 : specialOpeningHoursToday.openClosedTime) === null || _c === void 0 ? void 0 : _c.find(function (_a) {
                    var open = _a.open;
                    return getDateFromTime(open).getTime() > today.getTime();
                });
            }
            else if (regularOpeningHoursToday.length) {
                nextHours = regularOpeningHoursToday.find(function (_a) {
                    var open = _a.open;
                    return getDateFromTime(open).getTime() > today.getTime();
                });
            }
            if (nextHours) {
                return {
                    opensAt: 'TODAY',
                    hours: [[timeFormatter(getDateFromTime(nextHours.open))]],
                };
            }
        }
        if ((specialOpeningHoursTomorrow === null || specialOpeningHoursTomorrow === void 0 ? void 0 : specialOpeningHoursTomorrow.openClosed) === OpenClosedStatus.Open ||
            (regularOpeningHoursTomorrow === null || regularOpeningHoursTomorrow === void 0 ? void 0 : regularOpeningHoursTomorrow.length)) {
            if ((_d = specialOpeningHoursTomorrow === null || specialOpeningHoursTomorrow === void 0 ? void 0 : specialOpeningHoursTomorrow.openClosedTime) === null || _d === void 0 ? void 0 : _d.length) {
                nextHours = specialOpeningHoursTomorrow.openClosedTime[0];
            }
            else if ((specialOpeningHoursTomorrow === null || specialOpeningHoursTomorrow === void 0 ? void 0 : specialOpeningHoursTomorrow.openClosed) !== OpenClosedStatus.Closed &&
                regularOpeningHoursTomorrow.length) {
                nextHours = regularOpeningHoursTomorrow[0];
            }
            if (nextHours) {
                return {
                    opensAt: 'TOMORROW',
                    hours: [[timeFormatter(getDateFromTime(nextHours.open))]],
                };
            }
        }
    }
    return {
        hours: [],
    };
};

var OpenCloseIndication = function (_a) {
    var _b;
    var labels = _a.labels, status = _a.status, hours = _a.hours, isExpanded = _a.isExpanded, onExpand = _a.onExpand, onKeyDown = _a.onKeyDown, rest = __rest(_a, ["labels", "status", "hours", "isExpanded", "onExpand", "onKeyDown"]);
    return (React__default.createElement("button", __assign({}, rest, { "data-t": "OpenCloseIndication", className: classnames('open-close-indication', {
            'open-close-indication--expandable': Boolean(onExpand),
        }), tabIndex: Boolean(onExpand) ? 0 : -1, onClick: onExpand, onKeyDown: onKeyDown }),
        React__default.createElement("p", { className: classnames('open-close-indication__indication', (_b = {},
                _b["open-close-indication__indication--" + status.toLowerCase()] = true,
                _b)) },
            React__default.createElement("span", { className: "open-close-indication__status" }, labels.status),
            labels.indication,
            ' ', hours === null || hours === void 0 ? void 0 :
            hours.map(function (timePair) { return (React__default.createElement("span", { key: timePair.toString(), className: "open-close-indication__time-pair" }, timePair.length > 1 ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("time", { dateTime: timePair[0] }, timePair[0]), " - ",
                React__default.createElement("time", { dateTime: timePair[1] }, timePair[1]))) : (React__default.createElement("time", { key: timePair[0], dateTime: timePair[0] }, timePair[0])))); })),
        onExpand && (React__default.createElement(Icon, { className: "open-close-indication__icon", name: isExpanded ? 'chevron-up' : 'chevron-down' }))));
};

var OpenCloseIndicationContainer = function (_a) {
    var openingHours = _a.openingHours, specialOpeningHours = _a.specialOpeningHours, onExpand = _a.onExpand, rest = __rest(_a, ["openingHours", "specialOpeningHours", "onExpand"]);
    var _b = __read(React.useState(false), 2), mounted = _b[0], setMounted = _b[1];
    var _c = __read(React.useState(Date.now()), 2), time = _c[0], setTime = _c[1];
    var _d = useIntl(), dateTime = _d.dateTime, getLabel = _d.getLabel;
    var status = React.useMemo(function () { return getOpenCloseStatus(openingHours, specialOpeningHours); }, [
        // when the time state is set, it should trigger re-memoization
        time,
        openingHours,
        specialOpeningHours,
    ]);
    var indication = React.useMemo(function () { return getOpenCloseIndication(openingHours, specialOpeningHours, dateTime.formatTime); }, 
    // when the time state is set, it should trigger re-memoization
    [time, openingHours, specialOpeningHours, dateTime.formatTime]);
    var labels = React.useMemo(function () { return ({
        status: getLabel("locations.openCloseIndication.status." + status),
        indication: indication.opensAt
            ? getLabel("locations.openCloseIndication.indication." + indication.opensAt)
            : '',
    }); }, 
    // when the time state is set, it should trigger re-memoization
    [time, getLabel, indication.opensAt, status]);
    React.useEffect(function () {
        var interval = setInterval(function () {
            setTime(Date.now());
        }, 60000);
        return function () {
            clearInterval(interval);
        };
    }, []);
    React.useEffect(function () {
        setMounted(true);
    }, []);
    var onKeyDown = React.useCallback(function (event) {
        if (!onExpand) {
            return;
        }
        switch (event.key) {
            case 'ArrowLeft':
                onExpand(false);
                break;
            case 'ArrowRight':
                onExpand(true);
                break;
        }
    }, [onExpand]);
    return mounted ? (React.createElement(OpenCloseIndication, __assign({}, rest, { labels: labels, status: status, hours: indication.hours, onExpand: onExpand, onKeyDown: onKeyDown }))) : null;
};

var OpeningHours = function (_a) {
    var _b;
    var className = _a.className, labels = _a.labels, regular = _a.regular, openingHours = _a.openingHours, specialOpeningHours = _a.specialOpeningHours, _c = _a.special, special = _c === void 0 ? [] : _c, isExpanded = _a.isExpanded, onExpand = _a.onExpand, withTitle = _a.withTitle, rest = __rest(_a, ["className", "labels", "regular", "openingHours", "specialOpeningHours", "special", "isExpanded", "onExpand", "withTitle"]);
    return (React__default.createElement("div", __assign({}, rest, { className: classnames('location-opening-hours', (_b = {}, _b['location-opening-hours--expanded'] = isExpanded, _b), className) }),
        withTitle && (React__default.createElement(Heading, { variant: "smallest", component: "h2", className: "location-opening-hours__heading" }, labels.title)),
        React__default.createElement(OpenCloseIndicationContainer, { openingHours: openingHours, specialOpeningHours: specialOpeningHours, isExpanded: isExpanded, onExpand: onExpand }),
        React__default.createElement("div", { className: "location-opening-hours__lists" },
            (special === null || special === void 0 ? void 0 : special.length) ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Heading, { variant: "subheading2", component: "h3", className: "location-opening-hours__list-heading" }, labels.specialTitle),
                React__default.createElement("dl", { className: "location-opening-hours__list" }, special.map(function (_a) {
                    var label = _a.label, status = _a.status, entries = _a.entries;
                    return (React__default.createElement(React__default.Fragment, { key: label },
                        React__default.createElement("dt", { className: classnames('location-opening-hours__day') }, label),
                        React__default.createElement("dd", { className: classnames('location-opening-hours__hours', {
                                'location-opening-hours__hours--closed': status === 'CLOSED',
                            }) }, entries.map(function (_a) {
                            var key = _a.key, value = _a.value;
                            return (React__default.createElement("span", { key: key, className: "location-opening-hours__hours-entry" }, value));
                        }))));
                })),
                React__default.createElement(Heading, { variant: "subheading2", component: "span", className: "location-opening-hours__list-heading" }, labels.regularTitle))) : null,
            React__default.createElement("dl", { className: "location-opening-hours__list" }, regular.map(function (_a) {
                var label = _a.label, today = _a.today, status = _a.status, entries = _a.entries;
                return (React__default.createElement(React__default.Fragment, { key: label },
                    React__default.createElement("dt", { className: classnames('location-opening-hours__day', {
                            'location-opening-hours__day--today': today,
                        }) }, label),
                    React__default.createElement("dd", { className: classnames('location-opening-hours__hours', {
                            'location-opening-hours__hours--today': today,
                            'location-opening-hours__hours--closed': status === 'CLOSED',
                        }) }, entries.map(function (_a) {
                        var key = _a.key, value = _a.value;
                        return (React__default.createElement("span", { key: key, className: "location-opening-hours__hours-entry" }, value));
                    }))));
            })))));
};

// TODO: remove this once the deprecated props aren't used anymore
var deprecatedPropsToProps = function (props) {
    // Omit deprecated props from "rest"
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var schedule = props.schedule; props.highlightCurrentDay; var rest = __rest(props, ["schedule", "highlightCurrentDay"]);
    var openingHours = Object.entries(schedule).reduce(function (acc, _a) {
        var _b = __read(_a, 2), key = _b[0], value = _b[1];
        var day = Object.keys(acc).find(function (dayName) { return key.includes(dayName.slice(0, 3)) && key.includes('OpenClose'); });
        if (day && value) {
            acc[day] = value.map(function (_a) {
                var open = _a.open, close = _a.close;
                var openChars = __spreadArray([], __read(String(open)));
                var closeChars = __spreadArray([], __read(String(close)));
                if (!openChars.includes(':') && !openChars.includes('.')) {
                    openChars.splice(-2, 0, ':');
                }
                if (!closeChars.includes(':') && !closeChars.includes('.')) {
                    closeChars.splice(-2, 0, ':');
                }
                return {
                    open: openChars.join(''),
                    close: closeChars.join(''),
                };
            });
        }
        return acc;
    }, {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
    });
    return __assign({ openingHours: openingHours }, rest);
};

var isToday = function (day) { var _a; return ((_a = OPENING_HOURS_DAYS[new Date().getDay()]) !== null && _a !== void 0 ? _a : '') === day; };
var getDayName = function (date) { return OPENING_HOURS_DAYS[date.getDay()]; };
var hasDeprecatedProps = function (props) {
    return 'schedule' in props;
};
var OpeningHoursContainer = function (props) {
    var _a = hasDeprecatedProps(props) ? deprecatedPropsToProps(props) : props, openingHours = _a.openingHours, _b = _a.specialOpeningHours, specialOpeningHours = _b === void 0 ? [] : _b, _c = _a.isExpanded, initialIsExpanded = _c === void 0 ? false : _c, _d = _a.isExpandable, isExpandable = _d === void 0 ? true : _d, onExpandCallback = _a.onExpandCallback, rest = __rest(_a, ["openingHours", "specialOpeningHours", "isExpanded", "isExpandable", "onExpandCallback"]);
    var _e = __read(React__default.useState(initialIsExpanded), 2), isExpanded = _e[0], setIsExpanded = _e[1];
    var onExpandHandler = React__default.useCallback(function (doExpand) {
        setIsExpanded(function (current) { return (typeof doExpand === 'boolean' ? doExpand : !current); });
        if (typeof onExpandCallback === 'function') {
            onExpandCallback();
        }
    }, [onExpandCallback]);
    var _f = useIntl(), getLabel = _f.getLabel, formatMidsizeDate = _f.dateTime.formatMidsizeDate;
    var labels = React__default.useMemo(function () { return ({
        title: getLabel('locations.openingHours.title'),
        specialTitle: getLabel('locations.openingHours.specialTitle'),
        regularTitle: getLabel('locations.openingHours.regularTitle'),
    }); }, [getLabel]);
    var regular = React__default.useMemo(function () {
        return Object.entries(openingHours).map(function (_a) {
            var _b = __read(_a, 2), day = _b[0], items = _b[1];
            var isOpen = items.length > 0;
            var entries = isOpen
                ? items.map(function (_a) {
                    var open = _a.open, close = _a.close;
                    return ({
                        key: day + "-" + open + "-" + close,
                        value: open + " - " + close,
                    });
                })
                : [{ key: day + "-closed", value: getLabel('locations.openingHours.closed') }];
            return {
                label: getLabel("locations.openingHours.days.long." + day),
                today: isToday(day),
                status: isOpen ? 'OPEN' : 'CLOSED',
                entries: entries,
            };
        });
    }, [openingHours, getLabel]);
    var special = React__default.useMemo(function () {
        return (specialOpeningHours !== null && specialOpeningHours !== void 0 ? specialOpeningHours : []).map(function (_a) {
            var openClosed = _a.openClosed, dateOpenClosed = _a.dateOpenClosed, openClosedTime = _a.openClosedTime;
            var date = new Date(dateOpenClosed);
            var isOpen = openClosed === 'OPEN';
            var entries = isOpen
                ? (openClosedTime !== null && openClosedTime !== void 0 ? openClosedTime : []).map(function (_a) {
                    var open = _a.open, close = _a.close;
                    return ({
                        key: dateOpenClosed + "-" + open + "-" + close,
                        value: open + " - " + close,
                    });
                })
                : [
                    {
                        key: dateOpenClosed + "-closed",
                        value: getLabel('locations.openingHours.closed'),
                    },
                ];
            return {
                label: getLabel("locations.openingHours.days.short." + getDayName(date)) + ", " + formatMidsizeDate(date),
                status: openClosed,
                entries: entries,
            };
        });
    }, [specialOpeningHours, formatMidsizeDate, getLabel]);
    React__default.useEffect(function () { return setIsExpanded(initialIsExpanded); }, [initialIsExpanded]);
    return (React__default.createElement(OpeningHours, __assign({}, rest, { labels: labels, regular: regular, special: special, openingHours: openingHours, specialOpeningHours: specialOpeningHours, isExpanded: isExpanded, onExpand: isExpandable ? onExpandHandler : undefined })));
};

var UseMyLocationButton = function (_a) {
    var onClick = _a.onClick, caption = _a.caption, isLoading = _a.isLoading;
    return (React__default.createElement(Button, { size: "small", variant: "ghost", icon: "gps", className: "use-my-location", onClick: onClick, caption: caption, isLoading: isLoading }));
};

var LocationSearchMap = function (_a) {
    var className = _a.className, labels = _a.labels, markers = _a.markers, activeLocation = _a.activeLocation, mapOptions = _a.mapOptions, highlight = _a.highlight, hovered = _a.hovered, userLocation = _a.userLocation, userLocationZoomLevel = _a.userLocationZoomLevel, centerOnUserLocation = _a.centerOnUserLocation, onClickUseMyLocation = _a.onClickUseMyLocation, onMarkerClick = _a.onMarkerClick, onMarkerHover = _a.onMarkerHover, onMapClick = _a.onMapClick, center = _a.center, markersUpdateConfig = _a.markersUpdateConfig, id = _a.id, rest = __rest(_a, ["className", "labels", "markers", "activeLocation", "mapOptions", "highlight", "hovered", "userLocation", "userLocationZoomLevel", "centerOnUserLocation", "onClickUseMyLocation", "onMarkerClick", "onMarkerHover", "onMapClick", "center", "markersUpdateConfig", "id"]);
    return (React__default.createElement("div", __assign({}, rest, { className: classnames('location-search-map', className) }),
        React__default.createElement("style", { jsx: true, global: true }, "\n        .gm-style .gm-style-iw-t:after {\n          display: none;\n        }\n      "),
        React__default.createElement(GoogleMaps, { id: id, center: center, labels: { shareCurrentLocation: labels.shareCurrentLocation }, markers: markers, highlight: highlight, hovered: hovered, mapOptions: mapOptions, showUseMyLocation: true, userLocation: userLocation, userLocationZoomLevel: userLocationZoomLevel, centerOnUserLocation: centerOnUserLocation, markersUpdateConfig: markersUpdateConfig, onClickUseMyLocation: onClickUseMyLocation, onMarkerClick: onMarkerClick, onMarkerHover: onMarkerHover, onMapClick: onMapClick }),
        activeLocation && (React__default.createElement("div", { className: "location-search-map__active-location" }, activeLocation // we render the active location outside the GoogleMaps on mobile
        ))));
};

var LocationSearchMapContainer = function (props) {
    var getLabel = useIntl().getLabel;
    var labels = React__default.useMemo(function () { return ({
        shareCurrentLocation: getLabel('locations.search.map.shareCurrentLocation'),
    }); }, [getLabel]);
    var mapOptions = {
        minZoom: 3,
        maxZoom: 19,
        styles: [
            {
                featureType: 'poi.business',
                stylers: [{ visibility: 'off' }],
            },
        ],
    };
    return React__default.createElement(LocationSearchMap, __assign({}, props, { labels: labels, mapOptions: mapOptions }));
};

var defaultSettings = {
    enableHighAccuracy: false,
    timeout: Infinity,
    maximumAge: 0,
};
var useLocation = function (watch, options) {
    if (watch === void 0) { watch = false; }
    var settings = Object.assign({}, defaultSettings, options);
    var intl = useIntl();
    var _a = __read(React.useState(undefined), 2), position = _a[0], setPosition = _a[1];
    var _b = __read(React.useState(null), 2), error = _b[0], setError = _b[1];
    var _c = __read(React.useState(false), 2), isUseMyLocation = _c[0], setIsUseMyLocation = _c[1];
    var _d = __read(React.useState(false), 2), isMyLocationRequested = _d[0], setIsMyLocationRequested = _d[1];
    var _e = __read(React.useState(false), 2), isPositionLoading = _e[0], setIsPositionLoading = _e[1];
    var _f = __read(React.useState(false), 2), hasPermissionDeniedError = _f[0], setHasPermissionDeniedError = _f[1];
    var onChange = function (_a) {
        var coords = _a.coords, timestamp = _a.timestamp;
        setPosition({
            lat: coords.latitude,
            lng: coords.longitude,
            accuracy: coords.accuracy,
            timestamp: timestamp,
        });
        setIsPositionLoading(false);
    };
    var onError = function (error) {
        setIsPositionLoading(false);
        setIsUseMyLocation(false);
        console.warn('Failed to get user coords:', error.message);
        switch (error.code) {
            case error.PERMISSION_DENIED:
                setError(intl.getLabel('stores.yourLocation.permissionDenied'));
                toast.warning('stores.yourLocation.permissionDenied');
                break;
            default:
                setError(intl.getLabel('stores.yourLocation.error'));
                toast.warning('stores.yourLocation.error');
        }
    };
    var checkPermissionDeniedStatus = function (state) {
        setHasPermissionDeniedError(state === 'denied');
    };
    React.useEffect(function () {
        // Check first if geolocation permissions denied in the browser to show notification on button click.
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'geolocation' }).then(function (permissionStatus) {
                checkPermissionDeniedStatus(permissionStatus.state);
                // Check permissions if user change setting in the browser
                permissionStatus.addEventListener('change', function () {
                    checkPermissionDeniedStatus(permissionStatus.state);
                });
            });
        }
        else {
            // Internet Explorer, Safari, WebView Android and Safari on iOS do not support this feature by now,
            // set setHasPermissionDeniedError to false in this case.
            // In such browsers we will execute watchPosition or getCurrentPosition
            // to check and set correct value to hasPermissionDeniedError.
            setHasPermissionDeniedError(false);
        }
    }, []);
    React.useEffect(function () {
        if (!isMyLocationRequested) {
            setIsUseMyLocation(false);
        }
        if (isMyLocationRequested) {
            setError(null);
            if (hasPermissionDeniedError) {
                setError(intl.getLabel('stores.yourLocation.permissionDenied'));
                toast.warning('stores.yourLocation.permissionDenied');
                setIsMyLocationRequested(false);
                setIsPositionLoading(false);
                return;
            }
            var hasBrowserSupport = typeof window !== 'undefined' &&
                typeof window.navigator !== 'undefined' &&
                typeof window.navigator.geolocation !== 'undefined';
            if (!hasBrowserSupport) {
                setError(intl.getLabel('stores.yourLocation.unsupported'));
                toast.warning('stores.yourLocation.unsupported');
                setIsMyLocationRequested(false);
                return;
            }
            setIsPositionLoading(true);
            setIsUseMyLocation(true);
            var geo_1 = window.navigator.geolocation;
            var watcher_1 = null;
            if (watch) {
                watcher_1 = geo_1.watchPosition(onChange, onError, settings);
            }
            else {
                geo_1.getCurrentPosition(onChange, onError, settings);
            }
            return function () {
                watcher_1 && geo_1.clearWatch(watcher_1);
            };
        }
    }, [isMyLocationRequested, hasPermissionDeniedError]);
    return React.useMemo(function () { return ({
        position: position,
        isUseMyLocation: isUseMyLocation,
        isPositionLoading: isPositionLoading,
        isMyLocationRequested: isMyLocationRequested,
        error: error,
        setIsMyLocationRequested: setIsMyLocationRequested,
    }); }, [
        position,
        isUseMyLocation,
        isPositionLoading,
        isMyLocationRequested,
        error,
        setIsMyLocationRequested,
    ]);
};

var placePredictions = gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  fragment PlacePrediction on PlacePrediction {\n    id\n    locality\n    sublocality\n  }\n"], ["\n  fragment PlacePrediction on PlacePrediction {\n    id\n    locality\n    sublocality\n  }\n"])));
var GET_PLACE_PREDICTIONS = gql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  query GetPlacePredictions($name: String!) {\n    placePredictions(name: $name) {\n      ...PlacePrediction\n    }\n  }\n  ", "\n"], ["\n  query GetPlacePredictions($name: String!) {\n    placePredictions(name: $name) {\n      ...PlacePrediction\n    }\n  }\n  ", "\n"])), placePredictions);
var templateObject_1, templateObject_2;

var fromQueryPlacePredictionsToPlacePredictions = function (data) {
    if (!data) {
        return null;
    }
    return data.map(function (_a) {
        var id = _a.id, locality = _a.locality, sublocality = _a.sublocality;
        return ({
            id: id,
            locality: locality,
            sublocality: sublocality !== null && sublocality !== void 0 ? sublocality : undefined,
        });
    });
};

var usePlacePredictions = function (options) {
    var _a = __read(useLazyQuery(GET_PLACE_PREDICTIONS, __assign({}, options)), 2), getPlacePredictions = _a[0], _b = _a[1], error = _b.error, loading = _b.loading, data = _b.data;
    return React__default.useMemo(function () {
        return [
            getPlacePredictions,
            {
                data: fromQueryPlacePredictionsToPlacePredictions(data === null || data === void 0 ? void 0 : data.placePredictions),
                loading: loading,
                error: error,
            },
        ];
    }, [data, loading, error, getPlacePredictions]);
};

export { AddressContainer as Address, AddressConfigurationProvider, Location, LocationContactField, LocationDistanceContainer$1 as LocationDistance, LocationList, LocationNote, LocationSearchContainer as LocationSearch, LocationSearchList, LocationSearchListLayout, LocationSearchMapContainer as LocationSearchMap, LocationSearchTabsContainer as LocationSearchTabs, OpenCloseIndicationContainer as OpenCloseIndication, OpeningHoursContainer as OpeningHours, index as Types, UseMyLocationButton, getOpenCloseIndication, getOpenCloseStatus, useLocation, usePlacePredictions };
//# sourceMappingURL=index.js.map
